import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Toast from '../../components/common/Toast';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime, parseBackendDate } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';

const formatInputDate = (date) => {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 10);
};

const ScheduleManagementView = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedEngineerName, setSelectedEngineerName] = useState('Engineer');
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => formatInputDate(new Date()));
  const [toDate, setToDate] = useState(() => {
    const defaultToDate = new Date();
    defaultToDate.setDate(defaultToDate.getDate() + 7);
    return formatInputDate(defaultToDate);
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const { toasts, removeToast, error } = useToast();

  useEffect(() => {
    if (user?.id) {
      loadTicketsForSingleEngineer();
    }
  }, [user?.id]);

  const loadTicketsForSingleEngineer = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setSelectedEngineerName(user.name || 'Engineer');

    const ticketsResult = await engineerService.getMyTickets(user.id);
    if (ticketsResult.success) {
      setTickets(ticketsResult.data || []);
    } else {
      error(ticketsResult.error || 'Failed to load your ticket schedule');
    }

    setLoading(false);
  };

  const orderedTickets = useMemo(() => {
    const allowedStatuses = [
      TICKET_STATUSES.ASSIGNED, 
      TICKET_STATUSES.WORK_IN_PROGRESS,
      TICKET_STATUSES.REOPENED
    ];
    if (!fromDate || !toDate) {
      return [];
    }

    const startDate = new Date(`${fromDate}T00:00:00`);
    const endDate = new Date(`${toDate}T23:59:59.999`);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate > endDate) {
      return [];
    }

    const inScheduleWindow = (assignedDate) => {
      return assignedDate >= startDate && assignedDate <= endDate;
    };

    return tickets
      .filter((ticket) => allowedStatuses.includes(ticket.status))
      .filter((ticket) => ticket.assigned_for)
      .filter((ticket) => {
        const assignedDate = parseBackendDate(ticket.assigned_for);
        if (!assignedDate) {
          return false;
        }
        return inScheduleWindow(assignedDate);
      })
      .sort((a, b) => parseBackendDate(a.assigned_for) - parseBackendDate(b.assigned_for));
  }, [tickets, fromDate, toDate]);


  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type || 'Service'}
          </div>
        </div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value?.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'site_name',
      label: 'Site'
    },
    {
      key: 'assigned_for',
      label: 'Assigned Time',
      render: (value) => formatDateTime(value)
    }
  ];

  const handleViewTicket = (ticket) => {
    if (!ticket) {
      return;
    }
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading tickets list...
      </div>
    );
  }

  return (
    <div>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
        Showing tickets for: <strong style={{ color: 'var(--text-primary)' }}>{selectedEngineerName}</strong>
      </div>

      <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>From:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
        <label style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>To:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      </div>

      <DataTable
        data={orderedTickets}
        columns={columns}
        onRowClick={handleViewTicket}
        actions={[{ label: ' View', onClick: handleViewTicket }]}
        pageSize={15}
      />

      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTicket(null);
          }}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default ScheduleManagementView;
